import api from '@/api'
import Vue from 'vue'
import {vue} from '@/main.js'

export default {
  namespaced: true,
  state: {
    sessions: {},
    activeChat: null,
    chatMap: {}, // Map to store chat data by session ID
  },
  getters: {
    sessions(state) {
      return Object.values(state.sessions)
    },
    activeSession(state) {
      return state.activeChat
    },
    chatMap(state) {
      return state.chatMap
    },
    chatBySessionId: (state) => (sessionId) => {
      return state.chatMap[sessionId] || null
    },
    unseenMsgsBySessionId: (state) => (sessionId) => {
      return state.sessions[sessionId]?.unseenMsgs || 0
    },
    hasActiveChat(state) {
      return state.activeChat !== null
    },
    hasChats(state) {
      return Object.keys(state.chatMap).length > 0
    },
  },
  mutations: {
    setSessions(state, sessions) {
        state.sessions = (sessions || []).reduce((acc, session) => {
          acc[session.id] = session
          return acc
        }, {})
    },
    setSession(state, session) {
      state.sessions[session.id] = session
    },
    removeSession(state, sessionId) {
      delete state.sessions[sessionId]
      delete state.chatMap[sessionId]
      if(state.activeChat && state.activeChat.id === sessionId){
        state.activeChat = null
      }
    },
    setActiveChat(state, sessionId) {
      if(sessionId == null){
        state.activeChat = null
      }else {
        state.activeChat = state.sessions[sessionId]
      }
    },
    updateChat(state, messages) {
      if (messages == null || messages.length === 0) {
        return
      }
      let session = state.sessions[messages[0].chatSessionId]
      if (!session) {
        console.warn(`Session with ID ${messages[0].chatSessionId} not found in state.`)
        return
      }
      if (!state.chatMap[session.id]) {
        state.chatMap[session.id] = { userId: session.id, unseenMsgs: 0, messages: [], chatMessagesIds: new Set() }
      }
      let chatMessages =  state.chatMap[session.id].messages;
      let chatMessagesIds = new Set(chatMessages.map(m => m.messageId));
      // console.log("updateChat messages:", {chatMessagesIds, messages,chatMessages })
      messages.forEach(message => {
        if (!chatMessagesIds.has(message.messageId)) {
          chatMessages.push(message)
          chatMessagesIds.add(message.messageId)
        }
      })
      state.chatMap[session.id].messages = chatMessages
        .sort((a, b) => Vue.moment(a.timestamp).unix() - Vue.moment(b.timestamp).unix())
      state.chatMap[session.id].chatMessagesIds.add(...chatMessagesIds)
    },
    addMessage(state, message) {
        console.log('addMessage', message)
        let sessionId = message.sessionId
        if (!state.chatMap[sessionId]) {
            state.chatMap[sessionId] = { userId: sessionId, unseenMsgs: 0, messages: [], chatMessagesIds: new Set() }
        }
        let chatMessages = state.chatMap[sessionId].messages;
        let chatMessagesIds = state.chatMap[sessionId].chatMessagesIds;
        if (!chatMessagesIds.has(message.messageId)) {
            chatMessages.push(message)
            chatMessagesIds.add(message.messageId)
            vue.$root.$emit('system::chat::messages::updated',sessionId)
        }
    },
    updateMessage(state, {tempId, message, sessionId }) {
      console.log('updateMessage', { tempId, message })
      let session = state.chatMap[sessionId];
      let messagesIds = session.chatMessagesIds;
      let messages = session.messages;
      let existingMessageIndex = messages.findIndex(msg => msg.messageId === tempId)
      if (existingMessageIndex !== -1) {
        // Update existing message
        messages[existingMessageIndex] = { ...messages[existingMessageIndex], ...message, }
      } else {
        // Add new message
        messages.push(message)
      }
      messagesIds.add(message.messageId)
      vue.$root.$emit('system::chat::messages::updated', sessionId)
    },
    updateUnreadMessages(state, unreadBySessionMap) {
      delete unreadBySessionMap["total"];
      Object.values(state.sessions).forEach(session=>{
        session.unseenMsgs = unreadBySessionMap[session.id] || 0
      })
      Object.keys(unreadBySessionMap).forEach(sessionId =>{
        if (state.chatMap[sessionId] == null) {
            state.chatMap[sessionId] = { userId: sessionId, unseenMsgs: unreadBySessionMap[sessionId], messages: [], chatMessagesIds: new Set() }
        }
      })
    },
    clearActiveChat(state) {
      state.activeChat = null
    }, // Clear the currently active chat
    clearChats(state) {
      state.chatMap = {}
    }, // Clear all chats
  },
  actions: {
    fetchChatSessions({commit}) {
      return new Promise((resolve, reject) => {
        api.base()
          .get('/chat/sessions')
          .then(response => {
            let metadata = response.data.metadata || {}
            let sessions = response.data.data || []
            sessions.forEach(session => {
              session.unseenMsgs = metadata.unread ? (metadata.unread[session.id] || 0) : 0
            })
            commit('setSessions', sessions || [])
            resolve(sessions || [])
          })
          .catch(error => reject(error))
      })
    },
    fetchChatSession({commit}, sessionId) {
      return new Promise((resolve, reject) => {
        api.base()
          .get('/chat/sessions/'+sessionId)
          .then(response => {
            commit('setSession', response.data.data)
            resolve(response.data.data)
          })
          .catch(error => reject(error))
      })
    },
    initChatSession({ commit }, clientId) {
      return new Promise((resolve, reject) => {
        api.base()
          .post('/chat/session/init/'+clientId)
          .then(response => {
            commit('setSession', response.data.data)
            commit('setActiveChat', response.data.data.id)
            resolve(response.data.data)
          })
          .catch(error => reject(error))
      })
    },
    getChatMessages({commit}, { sessionId, page = 0, limit = 1000 }) {
        return new Promise((resolve, reject) => {
            api.base()
            .get(`/chat/messages/active/${sessionId}`, { params: { page, limit } })
            .then(response => {
                const messages = response.data.data || []
                commit('updateChat', messages)
                resolve(messages)
            })
            .catch(error => reject(error))
        })
    },
    sendChatMessage({ commit, getters, rootGetters }, { sessionId, messageRequest }) {
      let tempId = Vue.moment().unix() + Math.floor(Math.random() * 1000)
      let tempMessage = { ...messageRequest, messageId: tempId, isClientSide: false, clientHasSeen: false, timestamp: Vue.moment().format('YYYY-MM-DD HH:mm:ss'), sessionId };
      console.log('sendChatMessage', tempMessage)
      commit('addMessage', { ...tempMessage, senderId: rootGetters['user/myId'], sessionId });
      return api.base()
        .post(`/chat/messages/${sessionId}/send`, messageRequest)
        .then(response => {
          commit('updateMessage', { tempId: tempId, message: response.data.data, sessionId })
          return response.data
        }).finally(() => {})
    },
    fetchUnreadMessagesCount({ commit, getters }) {
        return new Promise((resolve, reject) => {
            api.base()
            .get('/chat/messages/unread/count')
            .then(response => {
                commit("updateUnreadMessages", {...(response.data.data||{})});
                resolve(response.data.data)
            })
            .catch(error => reject(error))
        })
    },
    setMessagesRead({ commit, getters }, { sessionId, messageIds }) {
        return new Promise((resolve, reject) => {
            api.base()
            .post(`/chat/session/${sessionId}/read`, { messageIds })
            .then(response => {
                const session = response.data.data;
                commit('setSession', session)
                resolve(session)
            })
            .catch(error => reject(error))
        })
    },
    finalizeSession({ commit, getters }, clientId) {
      return new Promise((resolve, reject) => {
        api.base()
          .post('/chat/session/finalize/' + clientId)
          .then(response => {
            commit('removeSession', response.data.data.id)
            resolve(response.data.data)
          })
          .catch(error => reject(error))
      })
    },
    notifyTypingMessage({ commit, getters }, sessionId) {
        return new Promise((resolve, reject) => {
            api.base()
            .post(`/chat/messages/${sessionId}/typing`, {})
            .catch(error => reject(error))
        })
    },
    getProfileUser() {
      return Promise.resolve({data:data.profileUser});
      /*return new Promise((resolve, reject) => {
        api.base()
          .get('/apps/chat/users/profile-user')
          .then(response => resolve(response))
          .catch(error => reject(error))
      })*/
    },
    getChat(ctx, { userId }) {
      // Convert Id to number
      userId = Number(userId)

      const chat = data.chats.find(c => c.userId === userId)
      if (chat) chat.unseenMsgs = 0

      return Promise.resolve({data:{ chat, contact: data.contacts.find(c => c.id === userId), }})
      /*return new Promise((resolve, reject) => {
        api.base()
          .get(`/apps/chat/chats/${userId}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })*/
    },
    sendMessage(ctx, { contactId, message, senderId }) {


      let activeChat = data.chats.find(chat => chat.userId === contactId)
      const newMessageData = {
        message,
        time: new Date(),
        senderId,
      }

      // If there's new chat for user create one
      let isNewChat = false
      if (activeChat === undefined) {
        isNewChat = true

        const { length } = data.chats
        const lastId = data.chats[length - 1].id

        data.chats.push({
          id: lastId + 1,
          userId: contactId,
          unseenMsgs: 0,
          chat: [],
        })
        activeChat = data.chats[data.chats.length - 1]
      }
      activeChat.chat.push(newMessageData)

      const response = { newMessageData }
      if (isNewChat) response.chat = activeChat
      return Promise.resolve({data:response})
      /*return new Promise((resolve, reject) => {
        api.base()
          .post(`/apps/chat/chats/${contactId}`, { message, senderId })
          .then(response => resolve(response))
          .catch(error => reject(error))
      })*/
    },
  },
}
